/* eslint-disable react/require-default-props, consistent-return */
import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { Send, PartyPopper } from 'lucide-react';
import Loading from '../../../components/Loading';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import GoalsConstants from '../../../constants/goals.constants';
import ReviewConstants from '../../../constants/review.constants';
import UserContext from '../../../hooks/context/UserContext';
import { AppLayout } from '../../../layouts';
import ApiService from '../../../services/api.service';
import axios from '../../../services/http.service';
import SocketService from '../../../services/socket.service';
import { checkIsEverySubSkillInProgress } from '../../Head/helpers/reviews.helpers';
import Congratulation from '../components/Congratulation';
import SkillTreePanel from './components/SkillTreePanel';
import SkillsPanel from './components/SkillsPanel';

function ReviewBanner({ message, onSendReview, isLoading }) {
  if (!message) return null;

  const showButton = message === ReviewConstants.MESSAGES.DO_YOU_WANT_TO_SEND_A_REVIEW_REQUEST;

  return (
    <Card className="border-primary/20 bg-primary/5">
      <CardContent className="flex items-center justify-between gap-4 p-4">
        <div className="flex items-center gap-3">
          <div className="rounded-full bg-primary/10 p-2">
            <PartyPopper className="h-5 w-5 text-primary" />
          </div>
          <p className="text-sm font-medium">{message}</p>
        </div>
        {showButton && (
          <Button onClick={onSendReview} disabled={isLoading} className="shrink-0">
            <Send className="h-4 w-4 mr-2" />
            Request Review
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

ReviewBanner.propTypes = {
  message: PropTypes.string,
  onSendReview: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

export default function HomeV2({ title }) {
  const [progress, setProgress] = useState(0);
  const [isReviewRequested, setIsReviewRequested] = useState(null);
  const [hardSkills, setHardSkills] = useState([]);
  const [softSkills, setSoftSkills] = useState([]);
  const [personalSkills, setPersonalSkills] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLastLevel, setIsLastLevel] = useState(false);
  const [isEverythingInProgress, setIsEverythingInProgress] = useState(false);
  const [congratulate, setCongratulate] = useState(false);
  const [reviewData, setReviewData] = useState(null);
  const [isLoadingBtn, setLoadingBtn] = useState(false);

  const { user, socketInstance, setSocketInstance } = useContext(UserContext);

  const handleOnRequestReviewClick = async () => {
    setLoadingBtn(true);
    const result = await ApiService.developer.requestReview();
    if (result.status) {
      setLoadingBtn(false);
      setIsReviewRequested(true);
    }
  };

  const handleLinkClick = useCallback(
    async (payload) => {
      if (!user) return;
      await axios({
        method: 'POST',
        url: 'users-history/task-link-clicked',
        data: {
          taskId: payload.taskId,
          link: payload.link,
        },
      });
    },
    [user],
  );

  const checkReview = useCallback(
    async (goalProgress) => {
      const data = await ApiService.developer.getRequestedReview();

      if (data) {
        setReviewData(data);
        setIsReviewRequested(true);
      } else {
        const confirmed = await ApiService.developer.getConfirmedReview();

        if (confirmed) {
          const foundNonCongratulated = confirmed.some((v) => !v.isCongratulated);

          if (foundNonCongratulated) {
            await ApiService.developer.setAllConfirmedAsCongratulated();
            setCongratulate(foundNonCongratulated);
          }
        }
      }

      if (goalProgress < 100) return;

      const [me, levels] = await Promise.all([
        ApiService.developer.getMySelf(),
        ApiService.developer.getDepartmentLevels(user.departmentId),
      ]);

      setIsLastLevel(_.last(levels)._id === me.level);
    },
    [user.departmentId],
  );

  useEffect(() => {
    async function fetchData() {
      try {
        setIsLoading(true);
        await axios({
          method: 'GET',
          url: 'goals',
        }).then((data) => {
          setProgress(data.progress);
          setHardSkills(data?.hardSkills);
          setSoftSkills(data?.softSkills);
          setPersonalSkills(data?.personalSkills);
          setIsLoading(false);

          const isProgress = checkIsEverySubSkillInProgress([
            ...data.hardSkills,
            ...data.softSkills,
            ...data.personalSkills,
          ]);

          setIsEverythingInProgress(isProgress);

          return checkReview(data.progress);
        });
      } catch (err) {
        setIsLoading(false);
        console.error(err);
      }
    }

    fetchData();
  }, [checkReview]);

  useEffect(() => {
    setSocketInstance(SocketService.getSocketInstance('goals'));

    return () => {
      socketInstance?.disconnect();
    };
  }, [setSocketInstance]);

  useEffect(() => {
    if (!socketInstance) return;

    const handler = async ({ goal, progress: actualProgress }) => {
      const { itemId, status, details, skillType, updatedStatusAt } = goal;

      setProgress(actualProgress);
      await checkReview(actualProgress);

      const updateSkills = (skills, setSkills) => {
        setSkills(
          skills.map((item) =>
            item.itemId === itemId ? { ...item, details, status, updatedStatusAt } : item,
          ),
        );
      };

      if (skillType === 'hard') updateSkills(hardSkills, setHardSkills);
      if (skillType === 'soft') updateSkills(softSkills, setSoftSkills);
      if (skillType === 'personal') updateSkills(personalSkills, setPersonalSkills);
    };

    socketInstance.on('receive-goal-status', handler);
    return () => socketInstance.off('receive-goal-status', handler);
  }, [hardSkills, softSkills, personalSkills, socketInstance, checkReview]);

  const goalsInProgress = useMemo(() => {
    const filteredHardSkills = hardSkills.filter(
      (goal) => goal.status === GoalsConstants.GOAL_STATUSES.IN_PROGRESS,
    );
    const filteredSoftSkills = softSkills.filter(
      (goal) => goal.status === GoalsConstants.GOAL_STATUSES.IN_PROGRESS,
    );
    const filteredPersonalSkills = personalSkills.filter(
      (goal) => goal.status === GoalsConstants.GOAL_STATUSES.IN_PROGRESS,
    );

    return [...filteredHardSkills, ...filteredSoftSkills, ...filteredPersonalSkills].sort(
      (a, b) => new Date(b.updatedStatusAt) - new Date(a.updatedStatusAt),
    );
  }, [hardSkills, softSkills, personalSkills]);

  const goalsDone = useMemo(() => {
    const filteredHardSkills = hardSkills.filter((goal) =>
      GoalsConstants.FILTERED_DONE_HARD_SKILL_STATUSES.includes(goal.status),
    );
    const filteredSoftSkills = softSkills.filter((goal) =>
      GoalsConstants.FILTERED_DONE_SOFT_SKILL_STATUSES.includes(goal.status),
    );
    const filteredPersonalSkills = personalSkills.filter((goal) =>
      GoalsConstants.FILTERED_DONE_PERSONAL_SKILL_STATUSES.includes(goal.status),
    );

    return [...filteredHardSkills, ...filteredSoftSkills, ...filteredPersonalSkills].sort(
      (a, b) => new Date(b.updatedStatusAt) - new Date(a.updatedStatusAt),
    );
  }, [hardSkills, softSkills, personalSkills]);

  const snackBarMessage = useMemo(() => {
    if (!isReviewRequested) {
      if (progress >= 100) {
        if (!isLastLevel) {
          if (!isEverythingInProgress) {
            return ReviewConstants.MESSAGES.DO_YOU_WANT_TO_SEND_A_REVIEW_REQUEST;
          }
          return ReviewConstants.MESSAGES.ALL_SKILLS_ARE_APPROVED;
        }
        return ReviewConstants.MESSAGES.YOU_GOT_ALL_LEVELS;
      }
    }

    if (isReviewRequested) {
      return ReviewConstants.MESSAGES.REVIEW_IS_REQUESTED_SUCCESSFULLY;
    }

    return null;
  }, [isEverythingInProgress, isLastLevel, isReviewRequested, progress]);

  if (isLoading && !_.isBoolean(isReviewRequested)) {
    return (
      <AppLayout title={title}>
        <Loading />
      </AppLayout>
    );
  }

  const hasBanner = !congratulate && snackBarMessage;

  return (
    <AppLayout title={title}>
      <div className="flex flex-col h-full">
        {/* Review Banner */}
        {hasBanner && (
          <div className="shrink-0 mb-6">
            <ReviewBanner
              message={snackBarMessage}
              onSendReview={handleOnRequestReviewClick}
              isLoading={isLoadingBtn}
            />
          </div>
        )}

        {/* Skills Grid */}
        <div className="grid gap-6 lg:grid-cols-2 flex-1 overflow-hidden">
          <SkillsPanel
            goalsInProgress={goalsInProgress}
            goalsDone={goalsDone}
            isDisabled={isReviewRequested}
            onLinkClick={handleLinkClick}
          />
          <SkillTreePanel
            hardSkills={hardSkills}
            softSkills={softSkills}
            personalSkills={personalSkills}
            isDisabled={isReviewRequested}
            reviewData={reviewData}
          />
        </div>
      </div>

      {/* Congratulation Modal */}
      <Congratulation congratulate={congratulate} handleClose={() => setCongratulate(false)} />
    </AppLayout>
  );
}

HomeV2.propTypes = {
  title: PropTypes.string.isRequired,
};
